/* Phase 2 admin panels for the Compliance Command Center. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Wand2, ShieldAlert, FileKey2 } from "lucide-react";

const inputCls = "rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cobalt/30";
const CONF_TONE = { high: "bg-emerald-50 text-emerald-600", medium: "bg-amber-50 text-amber-600", low: "bg-red-50 text-red-600" };

export const ClassificationAssistant = () => {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState({});
  const [conf, setConf] = useState("");
  const load = useCallback(() => {
    api.get("/compliance/classification/proposals", { params: conf ? { confidence: conf } : {} })
      .then(({ data }) => { setRows(data); setSel({}); }).catch(() => setRows([]));
  }, [conf]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    const { data } = await api.post("/compliance/classification/propose");
    toast.success(`${data.proposals_created} proposal(s) generated`); load();
  };
  const decide = async (ids, action, classification = null) => {
    if (!ids.length) { toast.error("Select proposals first"); return; }
    try {
      const { data } = await api.post("/compliance/classification/decide", { proposal_ids: ids, action, classification });
      if (data.blocked_low_confidence?.length) toast.warning(`${data.blocked_low_confidence.length} low-confidence proposal(s) BLOCKED from bulk approval — review them individually.`);
      else toast.success(`${action}: ${data.approved + data.rejected + data.legal_review} processed`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  if (rows === null) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  const selected = Object.keys(sel).filter((k) => sel[k]);
  return (
    <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="cc-classification">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><Wand2 className="w-4 h-4 text-cobalt" /> Classification Assistant <span className="text-slate-300 font-normal">({rows.length} pending)</span></h3>
          <p className="text-[11px] text-slate-400">Supervised workflow — low-confidence proposals can never be bulk-approved. Every decision is versioned and audited.</p>
        </div>
        <div className="flex gap-2 items-center">
          <select value={conf} onChange={(e) => setConf(e.target.value)} data-testid="cls-filter-confidence" className={inputCls}>
            <option value="">All confidence</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
          </select>
          <button onClick={generate} data-testid="cls-generate" className="px-4 py-2 rounded-full bg-navy text-white text-xs font-bold">Generate proposals</button>
        </div>
      </div>
      {selected.length > 0 && (
        <div className="mt-3 flex gap-2 items-center rounded-xl bg-cobalt/5 border border-cobalt/20 px-3 py-2">
          <span className="text-xs font-bold text-cobalt">{selected.length} selected</span>
          <button onClick={() => decide(selected, "approve")} data-testid="cls-approve-selected" className="px-3 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold">Approve selected</button>
          <button onClick={() => decide(selected, "reject")} data-testid="cls-reject-selected" className="px-3 py-1 rounded-full bg-red-500 text-white text-[10px] font-bold">Reject</button>
          <button onClick={() => decide(selected, "legal_review")} data-testid="cls-legal-selected" className="px-3 py-1 rounded-full bg-purple-500 text-white text-[10px] font-bold">Legal review required</button>
        </div>
      )}
      <div className="mt-3 max-h-[480px] overflow-y-auto space-y-1.5">
        {rows.map((p) => (
          <div key={p.proposal_id} className="flex items-center gap-3 rounded-xl border border-slate-100 px-3 py-2 text-xs" data-testid={`cls-row-${p.proposal_id}`}>
            <input type="checkbox" checked={!!sel[p.proposal_id]} onChange={(e) => setSel({ ...sel, [p.proposal_id]: e.target.checked })} data-testid={`cls-check-${p.proposal_id}`} className="accent-cobalt" />
            <div className="flex-1 min-w-0">
              <b className="text-navy">{p.asset_name}</b> <span className="text-slate-300">({p.asset_type})</span>
              <div className="text-slate-500 truncate">→ <b>{p.proposed_classification}</b> · {p.reason}</div>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${CONF_TONE[p.confidence]}`}>{p.confidence}</span>
            <button onClick={() => decide([p.proposal_id], "approve")} data-testid={`cls-approve-${p.proposal_id}`} className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold">Approve</button>
            <button onClick={() => { const c = window.prompt("New classification (exact name):", p.proposed_classification); if (c) decide([p.proposal_id], "edit", c); }} data-testid={`cls-edit-${p.proposal_id}`} className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">Edit</button>
          </div>
        ))}
        {!rows.length && <p className="text-xs text-slate-400 py-6 text-center">No pending proposals — generate a batch or all assets are classified.</p>}
      </div>
    </div>
  );
};

export const MfaTrack = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/compliance/mfa").then(({ data }) => setData(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  const p = data.policy;
  const save = async (patch) => {
    const { data: d } = await api.put("/compliance/mfa/policy", patch);
    setData(d); toast.success("MFA policy updated — control result reflects real enrollment only");
  };
  return (
    <div className="space-y-4" data-testid="cc-mfa-track">
      <div className={`rounded-3xl border p-5 ${data.operational ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
        <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> MFA Control Status: {data.operational ? "OPERATING" : "NOT OPERATING (honest)"}</h3>
        <p className="text-xs text-slate-600 mt-1">{data.honest_note}</p>
        <div className="mt-2 text-xs text-slate-600">Enforcement technically active at login: <b>{String(data.enforcement_technically_active)}</b> · Unenrolled enforced-role accounts: <b data-testid="mfa-unenrolled">{data.unenrolled_in_enforced_roles}</b></div>
      </div>
      <div className="rounded-3xl bg-white border border-slate-100 p-5">
        <h4 className="font-heading font-semibold text-navy text-sm">Enforcement policy</h4>
        <div className="mt-3 flex flex-wrap gap-3 items-end">
          <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={!!p.enabled} onChange={(e) => save({ enabled: e.target.checked })} data-testid="mfa-enabled" className="accent-cobalt" /> Enforcement enabled</label>
          <div><label className="text-[10px] font-bold text-slate-400 uppercase">Enforcement date</label>
            <input type="date" defaultValue={(p.enforcement_date || "").slice(0, 10)} onBlur={(e) => e.target.value && save({ enforcement_date: `${e.target.value}T00:00:00+00:00` })} data-testid="mfa-date" className={`${inputCls} block mt-1`} /></div>
          <div><label className="text-[10px] font-bold text-slate-400 uppercase">Grace days</label>
            <input type="number" defaultValue={p.grace_days} onBlur={(e) => save({ grace_days: Number(e.target.value) })} data-testid="mfa-grace" className={`${inputCls} block mt-1 w-24`} /></div>
        </div>
        <p className="text-[10px] text-slate-400 mt-2">Enforced roles: {(p.enforced_roles || []).join(", ")}. After the grace period, unenrolled users in these roles are blocked at login (real technical gate in /api/auth/login).</p>
        <h4 className="font-heading font-semibold text-navy text-sm mt-4">Enrollment by role (live)</h4>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-2">
          {Object.entries(data.role_stats).map(([role, s]) => (
            <div key={role} className="rounded-xl bg-slate-50 p-3 text-center" data-testid={`mfa-role-${role}`}>
              <div className="text-[10px] font-bold uppercase text-slate-400">{role}</div>
              <div className="font-heading font-extrabold text-navy">{s.enrolled}/{s.total}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const PrivacyRequestsAdmin = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/compliance/privacy-requests").then(({ data }) => setData(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  const act = async (rid, status) => {
    try { await api.put(`/compliance/privacy-requests/${rid}`, { status }); toast.success(`Status: ${status}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail?.message || e.response?.data?.detail) || "Blocked"); }
  };
  return (
    <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="cc-privacy-admin">
      <h3 className="font-heading font-bold text-navy text-sm">Privacy Requests <span className="text-slate-300 font-normal">({data.requests.length} total · {data.near_deadline} near deadline)</span></h3>
      <div className="mt-3 space-y-2 max-h-[520px] overflow-y-auto">
        {data.requests.map((r) => (
          <div key={r.request_id} className="rounded-xl border border-slate-100 px-4 py-3 text-xs" data-testid={`pra-${r.request_id}`}>
            <div className="flex justify-between items-center">
              <div><b className="text-navy capitalize">{r.request_type.replace(/_/g, " ")}</b> — {r.user_name} · {r.jurisdiction || "no jurisdiction"} </div>
              <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">{r.status}</span>
            </div>
            <div className="text-slate-400 mt-1">Due {new Date(r.deadline).toLocaleDateString()} ({r.deadline_basis}) · identity {r.identity_verified ? "✓ verified" : "NOT verified"}</div>
            {r.active_case_conflicts?.length > 0 && <div className="mt-1 text-red-500">Disposition conflicts: {r.active_case_conflicts.join("; ")}</div>}
            <div className="mt-2 flex gap-1.5 flex-wrap">
              {["Under Review", "Approved", "Denied", "Fulfilled", "Legal Review Required"].map((s) => (
                <button key={s} onClick={() => act(r.request_id, s)} data-testid={`pra-${r.request_id}-${s.replace(/ /g, "-")}`}
                  className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold hover:bg-navy hover:text-white">{s}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const SecurityDocsAdmin = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/compliance/security-docs").then(({ data }) => setData(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  const approve = async (rid) => {
    const password = window.prompt("Step-up authentication — enter your password to approve external access:");
    if (!password) return;
    try {
      const { data: g } = await api.post(`/compliance/security-docs/${rid}/approve`, { password, days: 7 });
      toast.success(`Approved — expiring watermarked link created (expires ${g.expires.slice(0, 10)})`);
      window.prompt("Share this one link with the requester:", `${window.location.origin}${g.access_path}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="space-y-4" data-testid="cc-security-docs">
      <div className="rounded-3xl bg-white border border-slate-100 p-5">
        <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><FileKey2 className="w-4 h-4 text-cobalt" /> Security-document requests</h3>
        <div className="mt-3 space-y-2">
          {data.requests.map((r) => (
            <div key={r.doc_request_id} className="rounded-xl border border-slate-100 px-4 py-3 text-xs flex flex-wrap justify-between items-center gap-2" data-testid={`sda-${r.doc_request_id}`}>
              <div>
                <b className="text-navy">{r.requester_name}</b> · {r.organization} · {r.email}
                <div className="text-slate-400">{r.requested_document} — "{r.business_reason}" · NDA: {r.nda_status}</div>
              </div>
              <div className="flex gap-1.5 items-center">
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">{r.status}</span>
                {r.status === "pending" && <button onClick={() => approve(r.doc_request_id)} data-testid={`sda-approve-${r.doc_request_id}`} className="px-3 py-1 rounded-full bg-navy text-white text-[10px] font-bold">Approve (step-up)</button>}
                {r.grant && <button onClick={async () => { await api.post(`/compliance/security-docs/grants/${r.grant.grant_id}/revoke`); toast.success("Access revoked"); load(); }} data-testid={`sda-revoke-${r.doc_request_id}`} className="px-3 py-1 rounded-full bg-red-50 text-red-500 text-[10px] font-bold">Revoke</button>}
              </div>
            </div>
          ))}
          {!data.requests.length && <p className="text-xs text-slate-400 py-4 text-center">No requests yet. The form lives on the Trust Center page.</p>}
        </div>
      </div>
      <div className="rounded-3xl bg-white border border-slate-100 p-5">
        <h4 className="font-heading font-semibold text-navy text-sm">Access log (every view &amp; download)</h4>
        <div className="mt-2 space-y-1 text-[11px] text-slate-500 max-h-56 overflow-y-auto">
          {data.access_log.map((l) => (
            <div key={l.log_id} className="flex justify-between rounded-lg bg-slate-50 px-3 py-1.5">
              <span>{l.requester} · {l.document} · {l.result}</span><span className="text-slate-300">{new Date(l.at).toLocaleString()} · {l.ip}</span>
            </div>
          ))}
          {!data.access_log.length && <p className="text-slate-300">No access events yet.</p>}
        </div>
      </div>
    </div>
  );
};
